import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  useStoreSelectionStore,
  selectCurrencySymbol,
} from "@/stores/store-selection-store";
import { useShallow } from "zustand/react/shallow";
import { useCartData } from "@/hooks/useCartData";
import { StoreIcon } from "@/components/icons/SvgIcons";
import { BasketButton } from "@/shared/components/BasketButton";
import { BasketStepper } from "@/components/basket";
import { testAttrs } from "@/utils/testAttrs";

const BASKET_STEPS = [
  { id: 1, label: "Basket" },
  { id: 2, label: "Checkout" },
  { id: 3, label: "Confirmation" },
];

interface SecondaryNavigationProps {
  isScrolled?: boolean;
  showShadow?: boolean;
}

export default function SecondaryNavigation({
  isScrolled = false,
  showShadow = false,
}: SecondaryNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { storeId, storeName, service, postcode } = useStoreSelectionStore(
    useShallow((s) => ({
      storeId: s.storeId || "",
      storeName: s.storeName || "Store",
      service: s.service || "delivery",
      postcode: s.postcode || "",
    })),
  );

  const { cartCount, formattedTotal } = useCartData();
  const currencySymbol = useStoreSelectionStore(selectCurrencySymbol);

  const orderType = service.toLowerCase();
  const resolvedStoreName = storeName || "Store";

  const getActiveTab = (): "menu" | "deals" | "deal-detail" | null => {
    const pathname = location.pathname;
    if (
      pathname === "/menu" ||
      pathname.startsWith("/pizza-details") ||
      pathname.startsWith("/chicken-dip-details")
    )
      return "menu";
    if (pathname === "/deals" || pathname.startsWith("/deal-detail"))
      return "deals";
    return null;
  };

  const activeTab = getActiveTab();

  const getBasketStep = (): number | null => {
    const pathname = location.pathname;
    if (pathname.startsWith("/basket")) return 1;
    if (pathname.startsWith("/checkout") || pathname.startsWith("/payment"))
      return 2;
    if (pathname.startsWith("/order-confirmation")) return 3;
    return null;
  };

  const isPaymentPage = location.pathname.startsWith("/payment");
  const isCheckoutPage =
    location.pathname.startsWith("/checkout") || isPaymentPage;
  const basketStep = getBasketStep();

  return (
    <div
      className={`bg-white w-full text-black sticky top-[118px] z-40 border-t border-[#f5f5f5] desktop:border-none ${showShadow ? "shadow-[0_4px_8px_-1px_rgba(0,0,0,0.35)] [clip-path:inset(0_0_-20px_0)]" : "desktop:shadow-[0_4px_8px_-1px_rgba(0,0,0,0.35)]"}`}
    >
      {/*
       * Collapsing MENU/DEALS row — only THIS section collapses on scroll
       * (mobile/tablet). The BasketStepper below stays outside this clip so it
       * is never hidden. On desktop nothing collapses.
       */}
      <div
        className={`overflow-hidden desktop:overflow-visible transition-[max-height] duration-300 ease-in-out ${
          isScrolled ? "max-h-0 desktop:max-h-none" : "max-h-[48px] md:max-h-[80px] desktop:max-h-none"
        }`}
      >
      <div className="mx-auto w-full max-w-[1248px] flex flex-row items-center justify-between desktop:py-4 pl-4 pr-0 desktop:px-6">
        {/* Store info pill — desktop only */}
        <div
          className="hidden desktop:flex relative z-1 dominos-secondary items-center justify-start bg-dominos-blue text-white rounded-full pl-4 py-1 pr-1 text-base max-w-[380px] order-1 cursor-pointer hover:bg-dominos-blue-dark transition-colors"
          onClick={() => navigate("/store-information")}
        >
          <StoreIcon
            width={24}
            height={24}
            className="mr-3 text-white shrink-0"
          />
          <span className="text-[16px] uppercase px-4 shrink-0">
            {orderType === "delivery" ? "Delivery" : "Collection"}
          </span>
          <span className="ml-4 bg-dominos-blue-dark px-3 py-3 rounded-full text-sm truncate max-w-[180px] overflow-hidden"
            {...testAttrs({ "data-testid": "header-store-name" })}>
            {resolvedStoreName}
          </span>
        </div>

        {/*
         * Navigation — always in DOM so the flex layout never shifts.
         * On mobile: fades out when scrolled (MENU/DEALS move into the
         * Header's unified row centre via a crossfade).
         * On desktop (lg+): always fully visible.
         */}
        <nav
          className={`flex items-center justify-center flex-1 gap-2 order-2 desktop:absolute left-0 right-0 h-[40px] md:h-[72px] desktop:h-auto transition-opacity duration-300 desktop:opacity-100 desktop:pointer-events-auto ${
            isScrolled ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <Link
            to={`/menu?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
            {...testAttrs({ "data-testid": "nav-tab", "data-tab-name": "menu" })}
            className={`px-4 md:px-[36px] desktop:px-12 h-[40px] md:h-[72px] desktop:h-auto pt-[8px] pb-[8px] nav-font text-[14px] md:text-[36px] desktop:text-[36px] uppercase whitespace-nowrap transition-colors relative ${
              activeTab === "menu"
                ? "text-dominos-red-bright border-b-2 border-dominos-blue"
                : "text-dominos-gray hover:text-dominos-red border-b-2 border-transparent"
            }`}
          >
            Menu
            {activeTab === "menu" && (
              <div
                className="absolute z-50 left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 pointer-events-none"
                style={{
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: "8px solid hsl(var(--dominos-blue))",
                }}
              ></div>
            )}
          </Link>
          <Link
            to={`/deals?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
            {...testAttrs({ "data-testid": "nav-tab", "data-tab-name": "deals" })}
            className={`px-4 md:px-[36px] desktop:px-12 h-[40px] md:h-[72px] desktop:h-auto pt-[8px] pb-[8px] nav-font text-[14px] md:text-[36px] hover:text-dominos-red desktop:text-[36px] uppercase whitespace-nowrap transition-colors relative ${
              activeTab === "deals"
                ? "text-dominos-red-bright border-b-2 border-dominos-blue"
                : "text-dominos-gray hover:text-dominos-red border-b-2 border-transparent"
            }`}
          >
            Deals
            {activeTab === "deals" && (
              <div
                className="absolute z-50 left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 pointer-events-none"
                style={{
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: "8px solid hsl(var(--dominos-blue))",
                }}
              ></div>
            )}
          </Link>
        </nav>

        {/* Basket button — desktop only */}
        <BasketButton
          to={`/basket?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
          count={cartCount}
          text={isCheckoutPage ? "Checkout" : "Basket"}
          total={formattedTotal}
          currencySymbol={currencySymbol}
          className="hidden desktop:flex order-3"
          disabled={isCheckoutPage}
          testId="basket-button"
        />
      </div>
      </div>

      {basketStep !== null && (
        <BasketStepper
          steps={BASKET_STEPS}
          currentStep={basketStep}
          paymentPage={isPaymentPage}
        />
      )}
    </div>
  );
}
